import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { RenterPolicyType } from "../components/RenterPolicyType";
import { RenterPolicyFrame } from "../components/RenterPolicyFrame";
import { RenterPolicyResourcePackage } from "../resources/RenterPolicyResourcePackage";
import { gsap } from "gsap";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { InputBlocker } from "../../../game/components/InputBlocker";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";

export class RenterPolicyPopupView extends BaseScalableView<void> {
    public static readonly RENTER_POLICY_TEXT = `RENTER'S\nQUEST RULE`;
    public static readonly RENTER_POLICY_NOTIFICATION = `RENTER'S QUEST\nNOTIFICATION`;

    private readonly renterPolicyResources: RenterPolicyResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly frameType: RenterPolicyType;

    private viewBackground: Sprite;
    private renterFrame: RenterPolicyFrame;
    private headerText: Text;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private closeButton: ButtonWithScaleAnimation;
    private animations: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        renterPolicyResources: RenterPolicyResourcePackage,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler,
        renterPolicyModel: IRenterPolicyModel,
        frameType: RenterPolicyType
    ) {
        super(operationControls, canvasScaler);
        this.renterPolicyResources = renterPolicyResources;
        this.commonResources = commonResources;
        this.renterPolicyModel = renterPolicyModel;
        this.frameType = frameType;
        this.animations = new gsap.core.Timeline({ paused: true });
        this.animations.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        await this.animations.play();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.addChild(this.viewBackground);
        this.renterFrame = new RenterPolicyFrame(this.renterPolicyResources, this.commonResources, this.renterPolicyModel, this.frameType);
        this.renterFrame.onFrameButtonClick.add(this.closeButtonClickHandle, this);
        this.addChild(this.renterFrame);
        this.headerText = new Text('', new HeaderTextStyle());
        this.headerText.skew._x = -0.087;
        this.headerText.angle = -5;
        this.addChild(this.headerText);
        switch (this.frameType) {
            case RenterPolicyType.RENTER_POLICY:
                this.headerText.text = RenterPolicyPopupView.RENTER_POLICY_TEXT;
                break;
            default:
                this.headerText.text = RenterPolicyPopupView.RENTER_POLICY_NOTIFICATION;
                break;
        }
        this.leftLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.leftLight.pivot.y = this.leftLight.height * 0.5;
        this.addChild(this.leftLight);
        this.rightLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight.scale.x = -1;
        this.rightLight.pivot.set(this.rightLight.width, this.rightLight.height * 0.5);
        this.addChild(this.rightLight);
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.closeButton.onUp.add(this.closeButtonClickHandle, this);
        this.addChild(this.closeButton);
    }

    protected layout(width: number, _height: number): void {
        switch (this.frameType) {
            case RenterPolicyType.RENTER_POLICY:
                this.renterFrame.position.set(57, 376);
                this.headerText.position.set(width * 0.5 - this.headerText.width * 0.5, 165);
                this.closeButton.position.set(width * 0.5 - this.closeButton.width * 0.5, 1649);
                break;
            case RenterPolicyType.DELETED_RENTED:
                this.renterFrame.position.set(57, 544.5);
                this.headerText.position.set(width * 0.5 - this.headerText.width * 0.5, 320);
                this.closeButton.position.set(width * 0.5 - this.closeButton.width * 0.5, 1630 - this.closeButton.height * 0.5);
                break;
            case RenterPolicyType.NOTIFICATION_DEFAULT:
                this.renterFrame.position.set(57, 337);
                this.headerText.position.set(width * 0.5 - this.headerText.width * 0.5, 148);
                this.closeButton.position.set(width * 0.5 - this.closeButton.width * 0.5, 1683 - this.closeButton.height * 0.5);
                break;
        }
        this.leftLight.position.set(0, this.renterFrame.y + this.renterFrame.height - this.leftLight.height * 0.25);
        this.rightLight.position.set(width - this.rightLight.width, this.renterFrame.y + this.renterFrame.height - this.rightLight.height * 0.25);
    }

    private async closeButtonClickHandle(): Promise<void> {
        const inputBlocker = new InputBlocker();
        inputBlocker.width = this.canvasScaler.referenceWidth;
        inputBlocker.height = this.canvasScaler.referenceHeight;
        this.addChild(inputBlocker);
        await this.hideView();
        this.removeChild(inputBlocker);
        this.operationControls.complete();
    }

    private async hideView(): Promise<void> {
        await this.animations.reverse();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.animations?.kill();
        this.renterFrame.onFrameButtonClick.remove(this.closeButtonClickHandle, this);
        this.closeButton.onUp.remove(this.closeButtonClickHandle, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#a8fd09",
                "#15ce14"
            ],
            fontSize: 140,
            align: "center",
            leading: -20
        }));
    }
}
